import React from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkStatsTotals } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../common/types';
import { DeltaPill } from '../components/primitives/DeltaPill';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { HomeTileDelta, flowDelta, formatSignedCount, pointChange } from './homeDeltas';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SmartlinksPerfTopProps {
  totals: SmartLinkStatsTotals;
  /** Null when the days before the window are outside the plan history: no change is shown. */
  previous: SmartLinkStatsTotals | null | undefined;
  chart: React.ReactNode;
}

/** Click-through (point change), visitors (percent) and pre-saves (count), then the per-day chart. */
const SmartlinksPerfTopComponent: React.FC<SmartlinksPerfTopProps> = ({ totals, previous, chart }) => {
  const intl = useIntl();

  const ctrChange = pointChange(totals.clickThroughRate, previous?.clickThroughRate ?? null);
  const ctrDelta: HomeTileDelta =
    ctrChange === null
      ? { kind: 'none' }
      : {
          kind: 'delta',
          value: ctrChange,
          label: intl.formatMessage(messages.smartlinksPointChange, {
            value: intl.formatNumber(ctrChange, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }),
          }),
        };
  const presaveDelta: HomeTileDelta =
    previous === undefined || previous === null
      ? { kind: 'none' }
      : {
          kind: 'delta',
          value: totals.presaves - previous.presaves,
          label: formatSignedCount(intl, totals.presaves - previous.presaves),
        };

  const stats = [
    {
      key: 'ctr',
      label: messages.smartlinksCtr,
      value:
        totals.clickThroughRate === null
          ? '—'
          : intl.formatNumber(totals.clickThroughRate / 100, { style: 'percent', maximumFractionDigits: 1 }),
      delta: ctrDelta,
    },
    {
      key: 'visitors',
      label: messages.smartlinksVisitors,
      value: intl.formatNumber(totals.visitors),
      delta: flowDelta(intl, totals.visitors, previous?.visitors ?? null),
    },
    {
      key: 'presaves',
      label: messages.smartlinksPresaves,
      value: intl.formatNumber(totals.presaves),
      delta: presaveDelta,
    },
  ];

  return (
    <div className={styles.perfTop}>
      {stats.map((stat) => (
        <div key={stat.key} className={styles.stat}>
          <small>{intl.formatMessage(stat.label)}</small>
          <span className={styles.statValue}>{stat.value}</span>
          {stat.delta.kind === 'delta' && (
            <span>
              <DeltaPill value={stat.delta.value} label={stat.delta.label} />
            </span>
          )}
          {stat.delta.kind === 'firstWeek' && (
            <span>
              <DeltaPill value={0} label={intl.formatMessage(messages.kpiFirstWeek)} />
            </span>
          )}
        </div>
      ))}
      {chart}
    </div>
  );
};

export const SmartlinksPerfTop = TypedMemo(SmartlinksPerfTopComponent);
